import { IntlShape } from 'react-intl';

import { MenuObjectTypes } from 'enums/menu';
import { CartEntryType } from 'generated/rbi-graphql';
import { IDayPartBoundary, IValidDayPart } from 'state/day-part/hooks/use-active-day-parts';
import { LoyaltyOffer, LoyaltyReward, isLoyaltyOffer, isReward } from 'state/loyalty/types';
import { IMenuItem, getMenuItemDayParts, isAvailableForActiveDayParts } from 'utils/availability';

const REWARD_ID_QUERY_PARAM = 'rewardId';

export const makeUrlForIncentive = (incentive: LoyaltyReward | LoyaltyOffer) => {
  // Ensure incentives are available
  if (!incentive.incentives?.length) {
    return;
  }
  // Find first incentive that is not a discount;
  const availableIncentive = incentive.incentives.find(
    singleIncentive => singleIncentive?._type !== MenuObjectTypes.OFFER_DISCOUNT
  );

  const { _type, _id } = { _id: null, ...availableIncentive } || {};

  if (!_type || !_id) {
    return;
  }

  const queryParam = isLoyaltyOffer(incentive)
    ? `isOffer=true`
    : `reward=${_id}&${REWARD_ID_QUERY_PARAM}=${incentive._id}`;

  return `/menu/${_type}-${_id}?${queryParam}`;
};

export const getRewardIdFromSearch = (search: string) => {
  const rewardId = new URLSearchParams(search).get(REWARD_ID_QUERY_PARAM);
  return rewardId;
};

export interface IMakeIncentiveUnavailableMessageArgs {
  incentive: LoyaltyReward | LoyaltyOffer;
  dayParts: ReadonlyArray<IValidDayPart>;
  formatMessage: IntlShape['formatMessage'];
  isAvailableForDayPart: boolean;
  stagedCartPoints: number;
  pointCost?: number;
  locked?: boolean | null;
}

interface ICheckIncentiveAvailableForDayPart {
  incentive: LoyaltyReward | LoyaltyOffer;
  activeDayParts: IDayPartBoundary[];
}

export const checkIncentiveAvailableForDayPart = ({
  incentive,
  activeDayParts,
}: ICheckIncentiveAvailableForDayPart) => {
  if (isLoyaltyOffer(incentive) && incentive.daypart?.length) {
    // TODO: This might be wrong on the Sanity schema, offer daypart should be an array
    return activeDayParts.some(dayPart => dayPart.key === incentive.daypart);
  }

  if (!incentive?.incentives?.length) {
    return true;
  }
  // For now get first incentive, future implementation TBD
  const [incentiveItem] = incentive.incentives;

  if (incentiveItem?._type === CartEntryType.OFFERDISCOUNT) {
    return true;
  }
  const result = isAvailableForActiveDayParts({
    activeDayParts,
    menuData: incentiveItem as IMenuItem,
  });

  return result;
};

export const makeIncentiveUnavailableMessage = ({
  dayParts,
  isAvailableForDayPart,
  formatMessage,
  locked,
  pointCost = 0,
  incentive,
  stagedCartPoints,
}: IMakeIncentiveUnavailableMessageArgs) => {
  if (locked) {
    return;
  }

  let message = undefined;

  if (!isAvailableForDayPart) {
    const getNextAvailableDayPart = (incentiveDayParts: string[]): IValidDayPart | undefined => {
      const incentiveDayPartsSet = new Set(incentiveDayParts);
      return dayParts.find(
        dayPart => dayPart.key && incentiveDayPartsSet.has(dayPart.key.toLowerCase())
      );
    };

    if (isReward(incentive) && incentive.incentives?.length) {
      const nextAvailableDayPart = getNextAvailableDayPart(
        getMenuItemDayParts(incentive.incentives[0] as IMenuItem)
      );
      message = nextAvailableDayPart
        ? formatMessage(
            { id: 'nonAvailableRewardUntilX' },
            {
              time: nextAvailableDayPart.startTime,
            }
          )
        : formatMessage({ id: 'nonAvailableReward' });
    } else if (isLoyaltyOffer(incentive) && incentive.daypart?.length) {
      // TODO: This might be wrong on the Sanity schema, offer daypart should be an array
      const nextAvailableDayPart = getNextAvailableDayPart([incentive.daypart.toLowerCase()]);
      message = nextAvailableDayPart
        ? formatMessage(
            { id: 'nonAvailableOfferUntil' },
            {
              time: nextAvailableDayPart.startTime,
            }
          )
        : formatMessage({ id: 'offerNotAvailable' });
    }
  }

  if (!message && stagedCartPoints < pointCost) {
    message = formatMessage({ id: 'exceedsRewardsBalance' });
  }

  return message;
};
